import {
  generateReceipt,
  TemplateVariables,
} from "@towersystems/roam-common/lib/generate-receipt";
import {
  AccountCountryType,
  ReceiptSettings,
  Sale,
} from "@towersystems/roam-common/lib/generated-types";
import ReceiptTemplate from "../data/model/ReceiptTemplate";
import { moneyToFormattedString } from "../money";
import SaleModel, { receiptHtmlMetadataKey } from "../data/model/Sale";
import { UseReceiptsControllerResponse } from "./types";
import { MUTATION_SEND_RECEIPT } from "./constants";
import { useMutation } from "@apollo/client";
import { useCallback } from "react";
import moment from "moment";
import { useGlobalSettingsProvider } from "../global-settings/hooks";
import { makeNegative } from "../make-negative";
import { formatPercentString } from "../format-percent-string";
import { guessChangePayment } from "../guess-change-payment";
import { useCurrentVendorProvider } from "../vendor";
import { bankersRound } from "@towersystems/roam-common/lib/shared-utils";

export const useReceiptsController = (): UseReceiptsControllerResponse => {
  const globalSettings = useGlobalSettingsProvider();
  const { currentVendor } = useCurrentVendorProvider();

  const generateReceiptHTML = async (
    maybeSaleModelOrEntity: Sale | SaleModel,
    receiptTemplate: ReceiptTemplate,
  ) => {
    const receiptTemplateType =
      await receiptTemplate.receiptTemplateType.fetch();

    const settings = receiptTemplate.settings;

    const variables =
      maybeSaleModelOrEntity instanceof SaleModel
        ? await saleModelToTemplateVariables(maybeSaleModelOrEntity, settings)
        : await saleEntityToTemplateVariables(maybeSaleModelOrEntity, settings);

    const showAbn = currentVendor?.countryType === AccountCountryType.AUSTRALIA;

    const receiptHtml = generateReceipt(
      {
        displayReceiptBarcode: true,
        logoUrl: settings.logoURL || undefined,
        //  businessName: globalSettings.businessName || "",
        businessAddress: `${globalSettings.address1 || ""}, ${
          globalSettings.postcode || ""
        } `,
        businessPhone: globalSettings.phone || "",
        businessEmail: globalSettings.email || "",
        businessABN: globalSettings.abn || "",
        businessWebsite: globalSettings.website || "",
        Header: settings.header,
        Footer: settings.footer,
        labelTax: settings.labelTax || "^ GST Total",
        labelABN: showAbn ? "ABN" : "GST Number",
        ...variables,
      },
      receiptTemplateType.html,
      receiptTemplateType.css,
    );

    return receiptHtml + variables.customerReceipts;
  };

  const saleEntityToTemplateVariables = async (
    sale: Sale,
    settings: ReceiptSettings,
  ): Promise<TemplateVariables> => {
    const maybeChangePayment = await guessChangePayment(sale);

    const paymentsWithoutChange = sale.payments.filter(
      (p) => p.id !== maybeChangePayment?.id,
    );

    const paymentTotals = paymentsWithoutChange.reduce(
      (acc, p) => acc + p.amount,
      0,
    );

    const isLayby = sale.saleType == "LAYBY";
    const isQuote = sale.saleType == "QUOTE";
    const isInvoice = sale.saleType == "INVOICE";

    let title = "Tax Invoice / Receipt";

    if (isLayby) {
      title = "Layby Tax Invoice / Receipt";
    } else if (isQuote) {
      title = "Quote";
    } else if (isInvoice) {
      title = "Invoice";
    }

    return {
      businessName: sale.location?.name,
      displayCustomerDetails:
        settings.displayCustomerDetails && Boolean(sale.customerId),
      title: title,
      saleDate: moment(sale.completedAt || new Date()).format("YYYY-MM-DD"),
      saleTime: moment(sale.completedAt || new Date()).format("HH:mm:ss"),
      hasSaleDiscount: sale.totalAdjustments > 0,
      siteName: sale.location?.name,
      saleNumber: sale.invoiceNumber,
      changeFormattedAmount: maybeChangePayment?.amount
        ? moneyToFormattedString(Math.abs(maybeChangePayment.amount))
        : undefined,
      laybyDepositFormattedAmount: isLayby
        ? moneyToFormattedString(paymentTotals)
        : undefined,
      laybyBalanceFormattedAmount: isLayby
        ? moneyToFormattedString(sale.balance)
        : undefined,
      saleDiscountFormattedPercentageDiscount: formatPercentString(
        sale.totalAdjustmentPercentSaving.toString() || "0",
      ),
      barcode: {
        code: sale.invoiceNumber,
        options: {
          text: ``,
          width: 3,
          height: 30,
          fontSize: 0,
          marginTop: 0,
          margin: 0,
        },
      },
      payments: paymentsWithoutChange.map((p) => {
        return {
          name: p?.name || "Unknown Payment Method",
          formattedAmount: moneyToFormattedString(p.amount),
        };
      }),
      totals: {
        discount: moneyToFormattedString(sale.totalAdjustments),
        tax: moneyToFormattedString(sale.totalTax),
        price: moneyToFormattedString(bankersRound(sale.totalPrice)),
      },
      staffName: sale.staff?.fullName,
      terminalName: sale.terminal?.name,
      customer: sale.customer
        ? {
            firstName: sale.customer?.firstName || "",
            lastName: sale.customer?.lastName || "",
            email: sale?.customer?.emailAddress,
          }
        : undefined,
      lineItems: sale.lineItems.map((li) => {
        return {
          quantity: li.quantity,
          productName: li.name ?? li.product?.name,
          formattedPrice: moneyToFormattedString(bankersRound(li.priceInclTax)),
          formattedOriginalPrice: moneyToFormattedString(
            bankersRound(li.basePrice),
          ),
          formattedTotalPrice: moneyToFormattedString(
            bankersRound(li.totalPriceInclusiveTax),
          ),
          hasDiscount: li.adjustments.length >= 1,
          productSKU: li.stockNumber
            ? li.stockNumber
            : li?.product?.stockNumber || "UUUUUUUUUUU",
          productVariantName: li?.product?.displayName,
          formattedUnitDiscount:
            li.adjustments.length >= 1
              ? moneyToFormattedString(li.totalAdjustments / li.quantity)
              : undefined,
          discountFormattedDollarDiscount: moneyToFormattedString(
            makeNegative(li.totalAdjustments),
          ),
          formattedTax: moneyToFormattedString(li.totalTax / li.quantity),
          customerNote: li.customerNote,
        };
      }),
      customerReceipts: [
        ...sale.payments
          .filter((p) => p.metadata?.customerReceipt)
          .map((p) => p.metadata?.customerReceipt),
        ...(sale?.metadata?.[receiptHtmlMetadataKey] ?? []),
      ],
    };
  };

  const saleModelToTemplateVariables = async (
    sale: SaleModel,
    settings: ReceiptSettings,
  ): Promise<TemplateVariables> => {
    const totals = await sale.totals_ASYNC();
    const customer = await sale.customer.fetch();
    const items = await sale.items.fetch();
    const staff = await sale.staff.fetch();
    const hasAdjustments = totals.totalDiscount > 0;
    const balance = await sale.balance_ASYNC();

    const maybeChangePayment = await guessChangePayment(sale);

    const paymentTotals = sale.payments.reduce((acc, p) => {
      return (acc += p.amount);
    }, 0);

    const paymentsWithoutChange = sale.payments.filter(
      (p) => p.localId !== maybeChangePayment?.id,
    );

    let title = "Tax Invoice / Receipt";

    if (sale.isLayby) {
      title = "Layby Tax Invoice / Receipt";
    } else if (sale.isQuote) {
      title = "Quote";
    } else if (sale.isInvoice) {
      title = "Invoice";
    }

    const islayby = sale.saleState.includes("LAYBY");
    const location = await sale.location.fetch();

    return {
      businessName: location?.name,
      displayCustomerDetails:
        settings.displayCustomerDetails && Boolean(sale.customer.id),
      title: title,
      laybyDepositFormattedAmount: islayby
        ? moneyToFormattedString(paymentTotals)
        : undefined,
      laybyBalanceFormattedAmount: islayby
        ? moneyToFormattedString(balance)
        : undefined,
      saleDate: moment(sale.saleCompletedAt || new Date()).format("YYYY-MM-DD"),
      saleTime: moment(sale.saleCompletedAt || new Date()).format("HH:mm:ss"),
      hasSaleDiscount: hasAdjustments,
      saleDiscountFormattedPercentageDiscount: formatPercentString(
        totals.totalDiscountPercent?.toString() || "0",
      ),
      siteName: (await sale.location.fetch()).name,
      saleNumber: sale.invoiceNumber,
      barcode: {
        code: sale.invoiceNumber || "",
        options: {
          text: ``,
          width: 3,
          height: 30,
          fontSize: 0,
          marginTop: 0,
          marginBottom: 0,
          margin: 0,
        },
      },
      payments: paymentsWithoutChange.map((p) => {
        return {
          name: p?.name || "Unknown Payment Method",
          formattedAmount: moneyToFormattedString(p.amount),
        };
      }),
      totals: {
        discount: moneyToFormattedString(totals.totalDiscount),
        tax: moneyToFormattedString(totals.totalTax),
        price: moneyToFormattedString(totals.totalPrice),
      },
      staffName: staff?.fullName,
      terminalName: (await sale.terminal.fetch())?.name,
      changeFormattedAmount: maybeChangePayment?.amount
        ? moneyToFormattedString(Math.abs(maybeChangePayment.amount))
        : undefined,
      customer: customer?.id
        ? {
            firstName: customer.firstName || "",
            lastName: customer.lastName || "",
            email: customer.emailAddress,
          }
        : undefined,
      lineItems: await Promise.all(
        items.map(async (li) => {
          const product = await li.product.fetch();
          const productKit = await li.productKit.fetch();

          let formattedComponentsSummary: string | undefined = undefined;

          if (!!productKit) {
            const items = await productKit.items.fetch();

            const componentSummary = await Promise.all(
              items.map(async (kitItem) => {
                const kitItemProduct = await kitItem.product.fetch();
                return `${kitItem.quantity}x ${kitItemProduct.name}`;
              }),
            );

            const componentsSummary = componentSummary?.join(", ");

            formattedComponentsSummary = componentsSummary
              ? `(${componentSummary})`
              : undefined;
          }

          return {
            quantity: li.quantity,
            productName: li.name ?? product?.name,
            formattedPrice: moneyToFormattedString(li.price),
            formattedTotalPrice: moneyToFormattedString(li.totalPrice),
            formattedOriginalPrice: moneyToFormattedString(li.basePrice),
            hasDiscount: li.adjustments.length >= 1,
            productSKU: li.stockNumber
              ? li.stockNumber
              : product.stockNumber || "UUUUUUUUUUU",
            productVariantName: product?.displayName,
            componentsSummary: formattedComponentsSummary,
            discountFormattedDollarDiscount: moneyToFormattedString(
              makeNegative(li.totalAdjustments),
            ),
            formattedUnitDiscount:
              li.adjustments.length >= 1
                ? moneyToFormattedString(li.totalAdjustments / li.quantity)
                : undefined,
            formattedTax: moneyToFormattedString(li.totalTax / li.quantity),
            customerNote: li.customerNote,
          };
        }),
      ),
      customerReceipts: [
        ...sale.payments
          .filter((p) => p.metadata?.customerReceipt)
          .map((p) => p.metadata?.customerReceipt),
        ...(sale?.metadata?.[receiptHtmlMetadataKey] ?? []),
      ],
    };
  };

  return {
    generateReceiptHTML,
  };
};

export const useEmailReceipt = () => {
  const [sendReceiptMutationFn] = useMutation<{ success: boolean }>(
    MUTATION_SEND_RECEIPT,
  );

  const emailReceipt = useCallback(
    async (
      saleId: string,
      to: string,
      receiptId?: string,
    ): Promise<boolean> => {
      const { data } = await sendReceiptMutationFn({
        variables: { input: { saleId, receiptId, to, type: "EMAIL" } },
      });
      return data?.success || false;
    },
    [],
  );

  return { emailReceipt };
};
